import type { Catalog } from '../types';

/**
 * As estatísticas — a tela de gráficos e o resumo da home.
 *
 * Cada gráfico diz **o que precisa** e **o que você tem** quando ainda não pode desenhar. As duas
 * frases são chaves separadas porque a primeira é uma regra e a segunda é um número: juntá-las num
 * molde só obrigaria a repetir a regra em cada ramo.
 */
export const stats = {
  'stats.title': { pt: 'Stats detalhados', en: 'Detailed stats' },
  'stats.subtitle': {
    pt: { one: '{count} volta de {holes} buracos', other: '{count} voltas de {holes} buracos' },
    en: { one: '{count} round of {holes} holes', other: '{count} rounds of {holes} holes' },
  },
  'stats.subtitleRange': { pt: '{base} · {range}', en: '{base} · {range}' },
  'stats.loading': { pt: 'carregando {done} de {total}', en: 'loading {done} of {total}' },

  'stats.empty.title': { pt: 'Nenhuma volta concluída ainda', en: 'No completed round yet' },
  'stats.empty.action': { pt: 'Registrar uma volta', en: 'Record a round' },
  'stats.empty.body': {
    pt: 'As estatísticas se montam a partir das voltas finalizadas. Assim que a primeira entrar, esta tela ganha conteúdo — e os gráficos aparecem sozinhos conforme cada um atinge seu mínimo.',
    en: 'Statistics are built from finished rounds. As soon as the first one lands, this screen fills in — and each chart appears on its own once it reaches its minimum.',
  },

  'stats.filtersAria': { pt: 'Recorte das estatísticas', en: 'Statistics window' },
  'stats.window.all': { pt: 'Todas', en: 'All' },
  'stats.window.last5': { pt: 'Últimas 5', en: 'Last 5' },
  'stats.holes': { pt: '{count} buracos', en: '{count} holes' },
  'stats.noMatch': {
    pt: 'Nenhuma volta de {holes} buracos no recorte escolhido.',
    en: 'No {holes}-hole round in the chosen window.',
  },
  'stats.noMatchCourse': {
    pt: 'Nenhuma volta de {holes} buracos neste campo no recorte escolhido.',
    en: 'No {holes}-hole round at this course in the chosen window.',
  },

  // -------------------------------------------------------------------- resumo

  'stats.summary': { pt: 'Resumo', en: 'Summary' },
  'stats.bestRound': { pt: 'Melhor volta', en: 'Best round' },
  'stats.averageScore': { pt: 'Score médio', en: 'Average score' },
  'stats.toParCaption': { pt: '{value} do par', en: '{value} to par' },
  'stats.bestDifferential': { pt: 'Melhor differential', en: 'Best differential' },
  'stats.countsForHandicap': { pt: 'entra no handicap', en: 'counts towards the handicap' },
  'stats.averageGir': { pt: 'GIR médio', en: 'Average GIR' },
  'stats.puttsPerRound': { pt: 'Putts / volta', en: 'Putts / round' },
  'stats.puttsPerHole': { pt: '{value} por buraco', en: '{value} per hole' },
  'stats.rounds': { pt: 'Voltas', en: 'Rounds' },
  'stats.noneOfOther': { pt: 'nenhuma de {other}', en: 'none of {other}' },
  'stats.moreOfOther': { pt: 'mais {count} de {other}', en: '{count} more of {other}' },

  // ------------------------------------------------------------------ evolução

  'stats.evolution': { pt: 'Evolução', en: 'Progress' },
  'stats.handicapChart': { pt: 'Evolução do handicap', en: 'Handicap over time' },
  'stats.handicapChartSub': {
    pt: 'Série montada com o handicap registrado no início de cada volta',
    en: 'Built from the handicap recorded at the start of each round',
  },
  'stats.needRoundsWithHandicap': {
    pt: 'Precisa de {count} voltas com handicap registrado',
    en: 'Needs {count} rounds with a recorded handicap',
  },
  'stats.handicapStartsAt': {
    pt: 'o handicap só passa a existir a partir de {count} voltas',
    en: 'a handicap only exists from {count} rounds on',
  },
  /* A mesma frase no perfil de outro jogador — "você tem 3 voltas" ali estaria falando da pessoa
     errada. É a única parte dos gráficos que sabe de quem é o assunto. */
  'stats.theyHave': {
    pt: { one: 'ele tem {count}', other: 'ele tem {count}' },
    en: { one: 'they have {count}', other: 'they have {count}' },
  },
  'stats.youHave': { pt: 'você tem {count}', en: 'you have {count}' },
  'stats.handicapAria': {
    pt: 'Handicap de {from} a {to} em {count} voltas',
    en: 'Handicap from {from} to {to} across {count} rounds',
  },

  'stats.differentials': { pt: 'Score differentials', en: 'Score differentials' },
  'stats.differentialsSub': {
    pt: 'Os 8 menores dos últimos 20 são exatamente os que definem seu índice',
    en: 'The lowest 8 of the last 20 are exactly the ones that set your index',
  },
  'stats.needCompletedRounds': {
    pt: 'Precisa de pelo menos {count} voltas concluídas',
    en: 'Needs at least {count} completed rounds',
  },
  'stats.differentialsAria': {
    pt: 'Differentials entre {min} e {max}; os que compõem o handicap estão destacados',
    en: 'Differentials between {min} and {max}; the ones making up the handicap are highlighted',
  },

  'stats.toPar': { pt: 'Score para o par', en: 'Score to par' },
  'stats.toParSub': { pt: 'Linha de referência na média', en: 'Reference line at the average' },
  'stats.averageAside': { pt: 'média {value}', en: 'average {value}' },
  'stats.countingLegend': { pt: 'compõem o handicap', en: 'make up the handicap' },
  'stats.discardedLegend': { pt: 'descartados', en: 'discarded' },
  'stats.needRounds': {
    pt: 'Precisa de {count} voltas concluídas',
    en: 'Needs {count} completed rounds',
  },
  'stats.toParAria': {
    pt: 'Score para o par ao longo de {count} voltas, média {mean}',
    en: 'Score to par across {count} rounds, average {mean}',
  },

  'stats.girFairway': { pt: 'GIR e Fairway', en: 'GIR and Fairway' },
  'stats.girFairwaySub': {
    pt: 'Eixo 0–70%. Fairway exclui os par 3, então as duas bases são diferentes',
    en: 'Axis 0–70%. Fairway excludes the par 3s, so the two bases differ',
  },
  'stats.girFairwayAria': {
    pt: 'GIR de {girFrom} a {girTo}, fairway de {fwFrom} a {fwTo}',
    en: 'GIR from {girFrom} to {girTo}, fairway from {fwFrom} to {fwTo}',
  },

  // ------------------------------------------------------------------- padrões

  'stats.patterns': { pt: 'Padrões', en: 'Patterns' },
  'stats.distribution': { pt: 'Distribuição de resultados', en: 'Score distribution' },
  'stats.distributionSub': {
    pt: 'Barra de 100% por volta — consistência diz mais que média',
    en: 'A 100% bar per round — consistency says more than an average',
  },
  'stats.distributionRowAria': {
    pt: 'Volta de {date} em {course}',
    en: 'Round on {date} at {course}',
  },
  'stats.courseFallback': { pt: 'campo', en: 'course' },

  'stats.byPar': { pt: 'Desempenho por par', en: 'Performance by par' },
  'stats.byParSub': {
    pt: 'Média acima do par, por tipo de buraco',
    en: 'Average over par, by hole type',
  },
  'stats.needOneRound': {
    pt: 'Precisa de pelo menos uma volta concluída',
    en: 'Needs at least one completed round',
  },
  'stats.haveNoneInWindow': {
    pt: 'você não tem nenhuma no recorte',
    en: 'you have none in this window',
  },
  'stats.byParAria': {
    pt: 'Média acima do par por tipo de buraco',
    en: 'Average over par by hole type',
  },
  'stats.parLabel': { pt: 'Par {par}', en: 'Par {par}' },
  'stats.parHoles': { pt: '{count} buracos', en: '{count} holes' },
  'stats.diagnosisStrong': {
    pt: 'você perde no par {par}',
    en: 'you lose strokes on the par {par}',
  },
  'stats.diagnosis': {
    pt: 'Diagnóstico direto: {strong} — média {average} por buraco, em {holes}.',
    en: 'Straight diagnosis: {strong} — {average} per hole on average, across {holes}.',
  },
  'stats.noDiagnosis': {
    pt: 'Nenhum tipo de buraco se destaca — os pares estão dentro de um quarto de tacada um do outro, o que é ruído, não padrão.',
    en: 'No hole type stands out — the pars sit within a quarter of a stroke of each other, which is noise, not a pattern.',
  },

  'stats.byHole': { pt: 'Desempenho por buraco', en: 'Performance by hole' },
  'stats.byHoleWithCourse': {
    pt: 'Desempenho por buraco — {course}',
    en: 'Performance by hole — {course}',
  },
  'stats.byHoleAside': {
    pt: {
      one: 'média acima do par em {count} volta neste campo',
      other: 'média acima do par em {count} voltas neste campo',
    },
    en: {
      one: 'average over par across {count} round at this course',
      other: 'average over par across {count} rounds at this course',
    },
  },
  'stats.needRoundsSameCourse': {
    pt: 'Precisa de {count} voltas no mesmo campo',
    en: 'Needs {count} rounds at the same course',
  },
  'stats.haveAtCourse': {
    pt: { one: '{course}: {count} volta', other: '{course}: {count} voltas' },
    en: { one: '{course}: {count} round', other: '{course}: {count} rounds' },
  },
  'stats.noCourseWithHistory': {
    pt: 'nenhum campo com histórico suficiente',
    en: 'no course with enough history',
  },
  'stats.byHoleAria': {
    pt: 'Média acima do par em cada buraco de {course}',
    en: 'Average over par on each hole at {course}',
  },
  'stats.byHoleSample': {
    pt: { one: 'em {count} volta neste campo', other: 'em {count} voltas neste campo' },
    en: { one: 'across {count} round at this course', other: 'across {count} rounds at this course' },
  },
  /**
   * "Os buracos **5** e **12** custam **+1,2** e **+0,9** — juntos, 31% do seu score acima do par."
   *
   * Os dois trechos em negrito são listas montadas em código (um ou dois buracos), então entram
   * como nós do React pelo `tx` — a frase guarda só a posição deles.
   */
  'stats.worstHoles': {
    pt: 'Os buracos {holes} custam {costs} — juntos, {share} do seu score acima do par',
    en: 'Holes {holes} cost you {costs} — together, {share} of your score over par',
  },
  'stats.and': { pt: ' e ', en: ' and ' },

  // ------------------------------------------------------- o resumo da home

  'home.title': { pt: 'Stats', en: 'Stats' },
  'home.detailed': { pt: 'Detalhados ▸', en: 'Detailed ▸' },
  'home.seeAll': { pt: 'Ver todas ▸', en: 'See all ▸' },
  'home.lastCompleted': { pt: 'Última volta concluída', en: 'Last completed round' },
  'home.lastRound': { pt: 'Última volta', en: 'Last round' },
  'home.cardMeta': {
    pt: '{date} · {holes} buracos · Tee {tee}',
    en: '{date} · {holes} holes · Tee {tee}',
  },
  'home.yourGame': { pt: 'Seu jogo', en: 'Your game' },
  'home.calculating': { pt: 'calculando…', en: 'calculating…' },
  'home.empty.title': { pt: 'Sem voltas ainda', en: 'No rounds yet' },
  'home.empty.body': {
    pt: 'Suas voltas do relógio aparecem aqui automaticamente. Também dá para registrar uma volta à mão.',
    en: 'Rounds from your watch show up here automatically. You can also record one by hand.',
  },
  'home.exploreCourses': { pt: 'Explorar campos', en: 'Explore courses' },

  'home.handicapIndex': { pt: 'Handicap index', en: 'Handicap index' },
  'home.basedOn': {
    pt: { one: 'Baseado em {count} volta', other: 'Baseado em {count} voltas' },
    en: { one: 'Based on {count} round', other: 'Based on {count} rounds' },
  },
  'home.updatedAt': { pt: '{base} · atualizado {date}', en: '{base} · updated {date}' },
  'home.handicapNext': {
    pt: 'Seu handicap será calculado na próxima atualização',
    en: 'Your handicap will be calculated at the next update',
  },
  'home.handicapMissing': {
    pt: { one: 'Falta {count} volta para seu handicap', other: 'Faltam {count} voltas para seu handicap' },
    en: { one: '{count} round to go for your handicap', other: '{count} rounds to go for your handicap' },
  },
  'home.updatedOn': { pt: 'Atualizado em', en: 'Updated on' },
  'home.handicapProgress': {
    pt: '{done} de {total} voltas concluídas',
    en: '{done} of {total} completed rounds',
  },

  'home.inProgress': { pt: 'Volta em andamento', en: 'Round in progress' },
  'home.startedAt': { pt: 'Iniciada {when} · Tee {tee}', en: 'Started {when} · Tee {tee}' },
  'home.ofTotal': { pt: 'de {total}', en: 'of {total}' },
  'home.viewRound': { pt: 'Ver volta', en: 'View round' },
  'home.unfinishedNote': {
    pt: 'Voltas não finalizadas não contam para o handicap.',
    en: 'Unfinished rounds do not count towards your handicap.',
  },
} as const satisfies Catalog;
